import logging from '@sstdev/lib_logging';
import { h5, useDebounce, useIsAnimating, View } from 'lib_ui-primitives';
import { dbViews as dbViewServices, globalConfig, ObjectId } from 'lib_ui-services';
import lodash from 'lodash';
import { Fragment, createElement as rc, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from 'styled-components';
import useDbView from '../../../hooks/useDbView';
import useEventSink from '../../../hooks/useEventSink';
import useMultiSelect from '../../../hooks/useMultiSelect';
import useNetwork from '../../../hooks/useNetwork';
import { getRequiredSelectionId } from '../../actionElement/helpers';
import useColumnWidths from '../columns/useColumnWidths';
import Grid from '../Grid';
import useColumnCustomization from '../useColumnCustomization';
import HeaderRow from './HeaderRow';
import useRowDetail from './useRowDetail';
const { omit } = lodash;

const { minFullTextSearchTermLength } = globalConfig();
const { LOCAL_ONLY_SEARCH_REASONS } = dbViewServices;

// What the last row says when the rows above it are not the whole answer to the user's search.
// The grid's usual "End of Records" claims the list is complete; on a size-limited relation
// searched without the server it is only complete as far as this device goes (#2040). Each of
// these says what the list actually ends, and the first one says how to get the rest.
const LOCAL_ONLY_END_OF_RECORDS_TEXT = {
    [LOCAL_ONLY_SEARCH_REASONS.termTooShort]:
        `End of records on this device. Type ${minFullTextSearchTermLength} or more characters to search all records.`,
    [LOCAL_ONLY_SEARCH_REASONS.serverUnavailable]:
        'End of records on this device. The server search is unavailable, so there may be more.',
    // No advice about typing here: this relation is never searched on the server at any term
    // length, so telling the user to type more would be wrong (#2047).
    [LOCAL_ONLY_SEARCH_REASONS.noServerSearch]:
        'End of records on this device. This list is not searched on the server, so there may be more.'
};

const _p = {
    useDbView,
    useDebounce,
    useColumnWidths,
    useNetwork,
    getLocationId,
    isNative: () => __SST_REACT_NATIVE__ || __TEST_RFID__
};
export const _private = _p;

function TableList(props) {
    const {
        hNode,
        hNode: {
            namespace,
            relation,
            id,
            rowClickAction = 'edit',
            includeCheckbox,
            singleRowSelect,
            endOfRecordsText = 'End of Records',
            // Opt-in only: a rule keys purely on verb/namespace/relation, with no way to tell which
            // page/grid a click came from, so broadcasting the selected location from every grid
            // would let it reach (and silently rewrite data for) any other grid on the same
            // namespace/relation. Only a grid the tenant has explicitly configured for this should
            // carry it (see #1792 PR review).
            attachSelectedLocationId = false
        },
        ...otherProps
    } = props || { hNode: {} };
    const [subscribe, publish] = useEventSink();
    const theme = useContext(ThemeContext);
    const { onIndividualChange } = useMultiSelect({ namespace, relation, singleRowSelect });

    const {
        records: _records,
        recordCount: _recordCount,
        viewCriteria,
        viewReady,
        requiresPostProcessing,
        calculateAggregateResult,
        awaitingFirstServerPage,
        localOnlySearchReason
    } = _p.useDbView(namespace, relation, undefined, hNode);

    // "End of Records" is only true when the rows above it are the whole answer. When the search
    // was answered off this device alone, say that instead (#2040). A grid that configures its own
    // endOfRecordsText away (an empty string on an embedded list) keeps that choice.
    const lastRowText = useMemo(() => {
        if (!endOfRecordsText || !localOnlySearchReason) return endOfRecordsText;
        return LOCAL_ONLY_END_OF_RECORDS_TEXT[localOnlySearchReason] ?? endOfRecordsText;
    }, [endOfRecordsText, localOnlySearchReason]);
    const locationId = attachSelectedLocationId ? _p.getLocationId(viewCriteria) : undefined;

    const [records, recordCount] = useMemo(() => {
        if (requiresPostProcessing) {
            const results = calculateAggregateResult(_records, _recordCount);
            return [results || [], results?.length || 0];
        }

        return [_records, _recordCount];
    }, [requiresPostProcessing, _records, _recordCount, calculateAggregateResult]);

    //keep track of which columns are asc/desc sorted
    const [sortedColumns, setSortedColumns] = useState({});
    const headerClicked = useCallback(
        function (column) {
            if (column.sortable) {
                setSortedColumns(prev => {
                    let nextValue = 'asc';
                    if (prev[column.id] === 'asc') {
                        nextValue = 'desc';
                    }
                    const path = `${column.propertyPath ? column.propertyPath + '.' : ''}${column.propertyName}`;
                    viewCriteria.addSort(path, nextValue === 'desc', `columnSort_${id}`);
                    return { ...prev, [column.id]: nextValue };
                });
            }
        },
        [viewCriteria, id]
    );

    // avoids rerenders of List
    const onClick = _p.useDebounce(
        item => {
            if (rowClickAction === 'nothing') return;
            if (singleRowSelect && includeCheckbox) {
                onIndividualChange(item._id, true);
            }
            if (item.inDatabase === false) {
                // unknown reads cannot be fetched from the database
                // we need to pass in what we have
                // there is only a "rowClickAction". Should we conform to that?
                // or should we always send a "new" action?
                //leaving in commented out code in case we change our mind:
                let data = { _id: item._id };
                // switch (rowClickAction) {
                //     case 'new':
                data.newRecord = omit(item, ['_id', 'name', 'rssi', 'sensorType', 'time']);
                //         break;
                //     case 'edit':
                //     default:
                //         data.record = item;
                //         break;
                // }
                publish(data, { verb: 'new', namespace, relation });
            } else {
                // for anything else, we want to make sure we fetch the latest form the database, so only pass in the _id
                const data = { _id: item._id };
                // Only ever attach locationId when this grid opted in -- present-but-null (rather
                // than omitted) tells the receiving rule "this grid cares about location, none is
                // currently selected", distinct from a grid that never opted in at all.
                if (attachSelectedLocationId) {
                    data.locationId = locationId ?? null;
                }
                publish(data, { verb: rowClickAction, namespace, relation });
            }
        },
        [publish, namespace, relation, rowClickAction, locationId, attachSelectedLocationId],
        350,
        {}
    );

    //used in lib_ui-primitives\src\components\List\RenderNotifyingRow.js (Which is never used...)
    const onRenderItem = useCallback(
        ({ item, updateItem, signal }) => {
            let aborted = false;
            const eventId = new ObjectId().toString();

            const unsubscribe = subscribe({ verb: 'didGet', namespace, relation, status: 'success' }, payload => {
                const {
                    result: [updatedItem]
                } = payload;

                if (item._id === updatedItem._id) {
                    if (!aborted) {
                        updateItem(updatedItem);
                    }
                }
            });
            signal.addEventListener('abort', () => {
                aborted = true;
                //unsubscribe event listeners here.
                unsubscribe();
            });
            publish({ result: [item] }, { verb: 'didGet', namespace, relation, eventId });
        },
        [subscribe, publish, namespace, relation]
    );

    // Avoids rerenders since this would otherwise create a new object reference every time.
    const style = useMemo(() => {
        return _p.isNative()
            ? {}
            : {
                  //always show at least the first column, AND the checkbox if there is one.
                  minWidth: `${includeCheckbox ? theme.maxTableColumnWidth + 42 : theme.maxTableColumnWidth}px`
              };
    }, [includeCheckbox, theme.maxTableColumnWidth]);

    // Get the width of each column from the column definition
    const { columnWidths, handleColumnWidthChange } = _p.useColumnWidths(props, records);
    // Pass the resulting column nodes with the widths to column customization
    const [columnHNodes, configurationPortal, configurationReady] = useColumnCustomization(props, columnWidths);

    const { isOnline, statusResolved } = _p.useNetwork();
    const [offlineTimestamp, setOfflineTimestamp] = useState(null);
    useEffect(() => {
        if (statusResolved && !isOnline) {
            setOfflineTimestamp(prev => prev ?? new Date().toLocaleString());
        } else {
            setOfflineTimestamp(null);
        }
    }, [isOnline, statusResolved]);
    const offlineText = offlineTimestamp ? `Device Connection Lost: ${offlineTimestamp}` : undefined;
    const [RowDetail, onScrollLayoutChange] = useRowDetail(hNode, columnHNodes, onClick, lastRowText, offlineText);
    // jsDom does not have a layout engine, so onScrollLayoutChange needs to be set manually during testing
    _p.onScrollLayoutChange = onScrollLayoutChange;
    const isAnimating = useIsAnimating();
    if (!viewReady || isAnimating || !configurationReady) {
        return rc(View, null, rc(h5, null, 'Loading...'));
    }
    logging.debug('[TableList]', 'render', { recordCount, time: performance.now(), namespace, relation });
    return rc(
        Fragment,
        null,
        configurationPortal,
        rc(Grid, {
            hNode,
            listType: 'table',
            records,
            recordCount,
            sortedColumns,
            columnHNodes,
            headerClicked,
            onClick,
            onRenderItem,
            RowDetail,
            HeaderRow,
            onScrollLayoutChange,
            style,
            endOfRecordsText: lastRowText,
            offlineText,
            awaitingFirstServerPage,
            localOnlySearchReason,
            handleColumnWidthChange,
            ...otherProps
        })
    );
}

export default TableList;

/**
 * Look at the configured filters on the viewCriteria, and IF there is one for
 * 'location:location._id', use that to return an _id.
 * If not, return nothing.
 * @param {*} viewCriteria
 * @returns {string | undefined}
 */
function getLocationId(viewCriteria) {
    return getRequiredSelectionId(viewCriteria, 'location', 'location');
}
